import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  Calendar,
  Compass,
  Map,
  MessageSquare,
  Menu,
  Moon,
  Mountain,
  Sparkles,
  Sun,
  User,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useTheme } from '@/hooks/useTheme';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { Button } from '@/components/ui/button';
import { NotificationsDropdown } from '@/components/layout/NotificationsDropdown';
import { ProfileMenu } from '@/components/layout/ProfileMenu';
import { TrailConnectNavMenu } from '@/components/layout/TrailConnectNavMenu';
import { ExploreNavMenu } from '@/components/layout/ExploreNavMenu';
import { KIT_CATEGORIES } from '@/features/trailkit/data/kit';
import { brand } from '@/lib/theme/tokens';

type NavLinkDef = {
  to: string;
  label: string;
  icon?: LucideIcon;
  /** Extra path prefixes that should also mark this link active. */
  matchPrefixes?: readonly string[];
};

const NAV_LINKS: readonly NavLinkDef[] = [
  { to: '/', label: 'Explore' },
  { to: '/adventure', label: 'Adventure' },
  { to: '/stays', label: 'Stays' },
  { to: '/trailkit', label: 'TrailKit', matchPrefixes: ['/vehicles'] },
  { to: '/guides', label: 'Guides' },
  { to: '/trailconnect', label: 'TrailConnect', matchPrefixes: ['/community', '/clubs', '/tribes'] },
  { to: '/about', label: 'About', matchPrefixes: ['/stories', '/seasons'] },
] as const;

export function Navbar() {
  const { mode, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const startOnboarding = useOnboardingStore((s) => s.start);

  const isLinkActive = (l: NavLinkDef, routerActive: boolean) =>
    routerActive ||
    (l.matchPrefixes?.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? false);

  function closeMobile() {
    setMobileOpen(false);
  }

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-4 top-4 z-50 mx-auto flex max-w-7xl items-center justify-between gap-3 rounded-full p-2 pl-4 transition-all duration-300',
          'glass-nav',
        )}
      >
        <Link to="/" className="flex items-center gap-2.5">
          <div
            className="grid h-8 w-8 place-items-center rounded-[10px]"
            style={{
              background: 'linear-gradient(135deg, var(--color-teal), var(--color-teal-dark))',
              boxShadow: '0 0 24px var(--color-teal-glow)',
            }}
          >
            <Compass className="h-4 w-4 text-white" strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-sm font-bold tracking-tight text-ink sm:text-base">
              Uncharted Odyssey
            </div>
            <div className="mt-0.5 hidden text-[9px] uppercase tracking-[0.2em] text-ink-mute sm:block">
              Kashmir · Curated
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => {
            // Explore gets a dropdown so Activities, Stays, Destinations, Seasons are discoverable.
            if (l.to === '/') return <ExploreNavMenu key={l.to} />;
            // TrailConnect gets a mega-menu so Community + Clubs stay discoverable.
            if (l.to === '/trailconnect') return <TrailConnectNavMenu key={l.to} />;

            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-medium transition-colors',
                    isLinkActive(l, isActive) ? 'bg-[var(--glass-hi)] text-ink' : 'text-ink-dim hover:text-ink',
                  )
                }
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="outline"
            size="sm"
            aria-label="Inbox"
            className="inline-flex h-9 w-9 rounded-full p-0"
          >
            <Link to="/community/chat">
              <MessageSquare className="h-4 w-4" />
            </Link>
          </Button>
          <NotificationsDropdown />
          <Button
            variant="outline"
            size="sm"
            onClick={toggle}
            aria-label="Toggle theme"
            className="hidden h-9 w-9 rounded-full p-0 sm:inline-flex"
          >
            {mode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <div className="hidden md:block">
            <ProfileMenu />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9 w-9 rounded-full p-0 lg:hidden"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            onClick={closeMobile}
            aria-hidden
          />
          <div
            data-lenis-prevent
            className="glass-hi absolute right-0 top-0 flex h-full w-[min(340px,88vw)] flex-col overflow-y-auto p-5 pt-6"
          >
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className="grid h-9 w-9 place-items-center rounded-[10px]"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-teal), var(--color-teal-dark))',
                    boxShadow: '0 0 24px var(--color-teal-glow)',
                  }}
                >
                  <Compass className="h-4 w-4 text-white" strokeWidth={2} />
                </div>
                <div className="leading-tight">
                  <div className="font-display text-[15px] font-bold tracking-tight text-ink">
                    Uncharted Odyssey
                  </div>
                  <div className="mt-0.5 text-[9px] uppercase tracking-[0.2em] text-ink-mute">
                    Kashmir · Curated
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={closeMobile}
                aria-label="Close menu"
                className="grid h-9 w-9 place-items-center rounded-full border border-[var(--glass-edge-hi)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Primary links */}
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((l) => {
                const Icon = l.icon;
                return (
                  <div key={l.to}>
                    <NavLink
                      to={l.to}
                      end={l.to === '/'}
                      onClick={closeMobile}
                      className={({ isActive }) =>
                        cn(
                          'inline-flex w-full items-center gap-2 rounded-xl px-3.5 py-2.5 text-[14px] font-medium transition-colors',
                          isLinkActive(l, isActive) ? 'bg-[var(--glass-hi)] text-ink' : 'text-ink-dim hover:text-ink',
                        )
                      }
                    >
                      {Icon && <Icon className="h-4 w-4" />}
                      {l.label}
                    </NavLink>

                    {l.to === '/' && (
                      <div className="ml-3 mt-1 grid gap-0.5 border-l border-glass-edge pl-3">
                        {[
                          { to: '/activities',  label: 'Activities',   sub: '40 treks, ski, rides & camps', icon: Mountain, color: brand.teal },
                          { to: '/destinations', label: 'Destinations', sub: '6 Kashmir zones',              icon: Map,      color: brand.chinar },
                          { to: '/seasons',     label: 'When to go',   sub: 'Month-by-month guide',          icon: Calendar, color: brand.cobalt },
                        ].map(({ to, label, sub, icon: SubIcon, color }) => (
                          <NavLink
                            key={to}
                            to={to}
                            onClick={closeMobile}
                            className={({ isActive }) =>
                              cn(
                                'inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-[12.5px] transition-colors',
                                isActive ? 'bg-[var(--glass-hi)] text-ink' : 'text-ink-dim hover:text-ink',
                              )
                            }
                          >
                            <SubIcon className="h-3 w-3 shrink-0" style={{ color }} />
                            <span className="truncate">{label}</span>
                            <span className="ml-auto text-[10px] text-ink-mute">{sub}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}

                    {l.to === '/trailconnect' && (
                      <div className="ml-3 mt-1 grid gap-0.5 border-l border-glass-edge pl-3">
                        <NavLink
                          to="/community"
                          onClick={closeMobile}
                          className={({ isActive }) =>
                            cn(
                              'inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-[12.5px] transition-colors',
                              isActive ? 'bg-[var(--glass-hi)] text-ink' : 'text-ink-dim hover:text-ink',
                            )
                          }
                        >
                          <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: brand.chinar }} />
                          Community
                        </NavLink>
                        <NavLink
                          to="/clubs"
                          onClick={closeMobile}
                          className={({ isActive }) =>
                            cn(
                              'inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-[12.5px] transition-colors',
                              isActive ? 'bg-[var(--glass-hi)] text-ink' : 'text-ink-dim hover:text-ink',
                            )
                          }
                        >
                          <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: brand.teal }} />
                          Clubs
                        </NavLink>
                      </div>
                    )}

                    {l.to === '/trailkit' && (
                      <div className="ml-3 mt-1 grid gap-0.5 border-l border-glass-edge pl-3">
                        {KIT_CATEGORIES.map((cat) => {
                          const CatIcon = cat.icon;
                          return (
                            <NavLink
                              key={cat.key}
                              to={cat.to}
                              onClick={closeMobile}
                              className={({ isActive }) =>
                                cn(
                                  'inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-[12.5px] transition-colors',
                                  isActive ? 'bg-[var(--glass-hi)] text-ink' : 'text-ink-dim hover:text-ink',
                                )
                              }
                            >
                              <CatIcon className="h-3 w-3 shrink-0" style={{ color: cat.color }} />
                              <span className="truncate">{cat.name}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Divider */}
            <div className="my-4 border-t border-glass-edge" />

            {/* Quick actions */}
            <div className="grid gap-1">
              <div className="px-3.5 text-[10px] font-bold uppercase tracking-[0.2em] text-ink-mute">
                Quick actions
              </div>
              <Link
                to="/community/chat"
                onClick={closeMobile}
                className="inline-flex w-full items-center gap-2 rounded-xl px-3.5 py-2.5 text-[13px] font-medium text-ink-dim transition-colors hover:bg-[var(--glass-hi)] hover:text-ink"
              >
                <MessageSquare className="h-4 w-4" />
                Inbox
              </Link>
              <Link
                to="/dashboard"
                onClick={closeMobile}
                className="inline-flex w-full items-center gap-2 rounded-xl px-3.5 py-2.5 text-[13px] font-medium text-ink-dim transition-colors hover:bg-[var(--glass-hi)] hover:text-ink"
              >
                <User className="h-4 w-4" />
                My dashboard
              </Link>
              <button
                type="button"
                onClick={() => {
                  closeMobile();
                  startOnboarding();
                }}
                className="inline-flex w-full items-center gap-2 rounded-xl px-3.5 py-2.5 text-left text-[13px] font-medium text-ink-dim transition-colors hover:bg-[var(--glass-hi)] hover:text-ink"
              >
                <Sparkles className="h-4 w-4" />
                Watch the tour
              </button>
              <button
                type="button"
                onClick={toggle}
                className="inline-flex w-full items-center gap-2 rounded-xl px-3.5 py-2.5 text-left text-[13px] font-medium text-ink-dim transition-colors hover:bg-[var(--glass-hi)] hover:text-ink"
              >
                {mode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {mode === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
            </div>

            {/* CTA */}
            <div className="mt-auto pt-5">
              <Button asChild className="w-full justify-center">
                <Link to="/auth/login" onClick={closeMobile}>
                  Sign in
                </Link>
              </Button>
              <div className="mt-2 text-center text-[10px] text-ink-mute">
                New here? <Link to="/auth/register" onClick={closeMobile} className="font-semibold text-ink underline">Create an account</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
